import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import React from 'react';

import { captureException } from '@sentry/nextjs';

import ArticlesList from 'components/ArticlesList/ArticlesList';
import BasePage from 'components/BasePage/BasePage';
import HeadWithSocialMedia from 'components/HeadWithSocialMedia/HeadWithSocialMedia';
import HorizontalRoundedCornerContent from 'components/HorizontalRoundedCornerContent/HorizontalRoundedCornerContent';
import Page404Content from 'components/Page404Content/Page404Content';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import Stack9Form from 'components/Stack9Form/Stack9Form';
import { IArticle } from 'models/article';
import { ArticleClientService } from 'services/articleClientService';
import { fetcher } from 'services/httpRequestor';

import MainLayout from '../../layouts/MainLayout';

type ArticleProps = {
  statusCode?: number;
  article?: IArticle;
  host: string;
};

// This gets called on every request
export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const articleService = new ArticleClientService(fetcher(req.headers.cookie));
  const { slug, ...getParams } = query;
  try {
    const { data: article, status } = await articleService.getOneByPageName(
      slug as string,
      {
        query: getParams,
      },
    );

    if (status === 404) {
      return {
        props: {
          slug,
          statusCode: 404,
        },
        notFound: true,
      };
    }
    // Pass data to the page via props
    return {
      props: {
        slug,
        statusCode: status,
        article,
      },
    };
  } catch (error) {
    captureException(error, { extra: { slug } });
    return {
      props: {
        slug,
        statusCode: (error as any).status || 500,
      },
    };
  }
};

const Article: React.FC<ArticleProps> = ({
  statusCode,
  article,
  host,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  if (statusCode >= 400) {
    return <Page404Content />;
  }
  const relatedArticles = article?.related_articles.map(
    ({ child }: { child: IArticle }) => child,
  );

  return (
    <>
      <HeadWithSocialMedia
        title={article?.title}
        description={article?.excerpt}
        thumbnailURL={article?.thumbnail_image?.url}
        url={`${host}/article/${article.slug}`}
        published_time={article?.published_datetime}
        canonical_url={article?.canonical_url}
      >
        <meta property="og:type" content="article" />
        <meta property="article:section" content="Article Section" />
        <meta property="fb:admins" content="Facebook numberic ID" />
      </HeadWithSocialMedia>
      <MainLayout>
        <BasePage
          page={{ ...article, page_blocks: article.article_blocks }}
          host={host}
          statusCode={statusCode}
        />
        {!!relatedArticles.length && (
          <SectionWithContainer>
            <div className="d-flex">
              <h2 className="heading heading-danger heading-small">
                Related Articles
              </h2>
            </div>
            <ArticlesList
              colProps={{ lg: 4, md: undefined }}
              articles={relatedArticles}
            />
          </SectionWithContainer>
        )}
        <SectionWithContainer>
          <HorizontalRoundedCornerContent>
            <HorizontalRoundedCornerContent.Col className="bg-primary">
              <h2 className="text-white heading heading-secondary w-100">
                Sign up for the newsletter and keep up to date.
              </h2>
              <Stack9Form
                formId={ENV.STACK9_NEWSLETTER_FORM_ID}
                height={ENV.STACK9_NEWSLETTER_FORM_HEIGHT}
                className="w-100"
              />
            </HorizontalRoundedCornerContent.Col>
            <HorizontalRoundedCornerContent.Col imgURL="/imgs/newsletter.jpg" />
          </HorizontalRoundedCornerContent>
        </SectionWithContainer>
      </MainLayout>
    </>
  );
};

export default Article;
